// Pure key-event → high-level action classifier. Lives next to InputHandler
// to keep that file under the 200-line cap and to make the dispatch table
// trivially unit-testable without DOM listeners.

import { isDigit, type NonEmptyDigit } from '../game/types.js';

export type ArrowDirection = 'up' | 'down' | 'left' | 'right';

export type KeyAction =
  | { kind: 'digit'; digit: NonEmptyDigit }
  | { kind: 'arrow'; direction: ArrowDirection }
  | { kind: 'erase' }
  | { kind: 'undo' }
  | { kind: 'redo' }
  | { kind: 'pencil-toggle' }
  | { kind: 'escape' };

/** Snapshot of the modifier flags we care about. Extracted so tests don't
 * need to fabricate a full KeyboardEvent. */
export interface KeyEventSnapshot {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly repeat: boolean;
}

const ARROW_KEYS: Readonly<Record<string, ArrowDirection>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

function isPlainModifier(e: KeyEventSnapshot): boolean {
  return !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
}

/**
 * Map a keyboard event to a sudoku action, or `null` if no action matches.
 * The handler is responsible for filtering focused inputs / repeats where
 * appropriate — this function is purely a key-pattern classifier.
 *
 * Keymap:
 *   1..9                    → digit
 *   Arrow{Up,Down,Left,Right} → arrow
 *   Backspace, Delete, 0    → erase
 *   z (no mods), ctrl/cmd+z → undo
 *   y (no mods), ctrl/cmd+y → redo
 *   ctrl/cmd+shift+z        → redo (mac/git convention)
 *   p (no mods)             → pencil-toggle
 *   Escape                  → escape
 */
export function classifyKey(e: KeyEventSnapshot): KeyAction | null {
  const direction = ARROW_KEYS[e.key];
  if (direction !== undefined) {
    return { kind: 'arrow', direction };
  }

  if (e.key === 'Escape') return { kind: 'escape' };
  if (e.key === 'Backspace' || e.key === 'Delete') return { kind: 'erase' };

  // Digits — only plain (no modifiers). `0` triggers erase to match the
  // common sudoku convention of "press 0 to clear".
  if (isPlainModifier(e) && e.key.length === 1) {
    if (e.key === '0') return { kind: 'erase' };
    const digit = Number(e.key);
    if (isDigit(digit)) return { kind: 'digit', digit };
  }

  const lower = e.key.toLowerCase();
  const ctrlOrMeta = e.ctrlKey || e.metaKey;

  if (lower === 'z') {
    if (ctrlOrMeta && e.shiftKey && !e.altKey) return { kind: 'redo' };
    if (ctrlOrMeta && !e.shiftKey && !e.altKey) return { kind: 'undo' };
    if (isPlainModifier(e)) return { kind: 'undo' };
  }

  if (lower === 'y') {
    if (!e.altKey && !e.shiftKey && (ctrlOrMeta || isPlainModifier(e))) {
      return { kind: 'redo' };
    }
  }

  if (lower === 'p' && isPlainModifier(e)) {
    return { kind: 'pencil-toggle' };
  }

  return null;
}
